import { useEffect, useMemo, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { PlusSmallIcon } from "@heroicons/react/24/outline";
import LocationGroupsTable from "../../components/locationGroups/LocationGroupsTable";
import useLocationGroups from "../../data/useLocationGroups";
import { ILocationGroup } from "../../models/LocationGroup";
import LocationGroupsModal from "../../components/locationGroups/LocationGroupsModal";

export default function LocationGroups() {
  const { setInfo } = usePageNameContext();
  const { locationGroups: allLocationGroups, availabilities } = useLocationGroups();
  const [locationGroups, setLocationGroups] = useState<ILocationGroup[] | undefined>(undefined);
  const [editLocationGroup, setEditLocationGroup] = useState<ILocationGroup | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setInfo({
      name: "Yard Sections",
      desc: "View and manage yard sections and their availability",
    });
  }, []);

  useEffect(() => {
    if (!allLocationGroups.isLoading && allLocationGroups.data) {
      setLocationGroups(allLocationGroups.data?.data);
      setIsLoading(false);
    }
  }, [allLocationGroups.isLoading, allLocationGroups.data]);

  const availabilityLookup = useMemo(() => {
    const list = availabilities.data?.data ?? [];

    return list.reduce<Record<string, { locationCount: number, capacity: number, vehicleCount: number }>>((acc, current) => {
      acc[current.locationGroupId] = {
        locationCount: current.locationCount,
        capacity: current.capacity,
        vehicleCount: current.vehicleCount,
      };
      return acc;
    }, {});
  }, [availabilities.data]);

  const handleSetLocationGroup = (locationGroup: ILocationGroup) => {
    setEditLocationGroup(locationGroup);
    setCreateOpen(true);
  };

  const handleAddLocationGroup = () => {
    setEditLocationGroup(undefined);
    setCreateOpen(true);
  }

  return allLocationGroups.isLoading || availabilities.isLoading || isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <>
      <LocationGroupsModal open={createOpen} setOpen={setCreateOpen} locationGroup={editLocationGroup} />
      <div className="mt-4 flex justify-between">

        {/* Button for adding a new section */}
        <button
          className=" bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
          type="button"
          onClick={handleAddLocationGroup}
        >
          <PlusSmallIcon className="h-5 w-5 mr-2" />
          Add New Section
        </button>

      </div>

      {/* Table of sections */}
      <LocationGroupsTable locationGroups={locationGroups!} availabilities={availabilityLookup} setLocationGroup={handleSetLocationGroup} />
    </>
  );
}
